import { createApp, type ComponentPublicInstance } from 'vue';
import App from './App.vue';
import 'quasar/dist/quasar.prod.css';
import 'intl-tel-input/build/css/intlTelInput.css';
import './styles/app.scss';
import './styles/tailwind.css';
import 'vue-advanced-cropper/dist/style.css';
import 'intl-tel-input/build/css/intlTelInput.css';
import { Quasar } from 'quasar';
import quasarUserOptions from './quasar-user-options';
import '@/boot/axios';
import { createPinia } from 'pinia';
import routes from '@/router';
import type { Router } from 'vue-router';
import components from '@/boot/components';
import directives from '@/boot/directives';
import fontAwesome from '@/boot/font-awesome';
import VueScrollTo from 'vue-scrollto';
import { createMetaManager } from 'vue-meta';
import { createGtm } from '@gtm-support/vue-gtm';
import { useAuthStore } from './store/authStore';
import 'vue-css-donut-chart/dist/vcdonut.css';
import * as Sentry from '@sentry/vue';
import { client } from '@/api';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';

const pinia = createPinia();
const app = createApp(App);
client.setConfig({
  baseUrl: import.meta.env.VITE_API_ROOT_URL,
});
if (import.meta.env.MODE !== 'production') {
  // Enable Vue devtools and performance tracking (properties not in type definitions)
  (
    app.config as unknown as { devtools: boolean; performance: boolean }
  ).devtools = true;
  (
    app.config as unknown as { devtools: boolean; performance: boolean }
  ).performance = true;
}

/** Sentry setup */
(() => {
  const sentryMeta = (() => {
    if (import.meta.env.MODE !== 'production') return null;

    const stage = import.meta.env.VITE_STAGE || 'dev';
    const seedBuildId =
      import.meta.env.VITE_SEED_BUILD_ID || 'local_or_unknown';

    const releaseName = `${stage}_${seedBuildId}`;

    const DEV_DSN =
      'https://218b177fb3b3f6157c5487894751f263@o4507583776555008.ingest.us.sentry.io/4507589565153280';
    const PROD_DSN =
      'https://beb524364870e65bb7eee8f3d450e7fb@o4507583776555008.ingest.us.sentry.io/4507583785140224';
    const dsn = stage === 'prod' ? PROD_DSN : DEV_DSN;

    const isDevSite = stage !== 'prod';

    return {
      dsn,
      releaseName,
      isDevSite,
    };
  })();

  if (sentryMeta) {
    Sentry.init({
      app,
      dsn: sentryMeta.dsn,
      release: sentryMeta.releaseName,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        new RegExp(import.meta.env.VITE_API_ROOT_URL),
      ],
      // Session Replay
      replaysSessionSampleRate: sentryMeta.isDevSite ? 1.0 : 0.1, // Capture 100% of the sessions on dev sites, 10% on production sites
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  }
})();

// Register all community modules with ag-grid
ModuleRegistry.registerModules([AllCommunityModule]);

// Standard error handler for Vue
app.config.errorHandler = (
  err: unknown,
  instance: ComponentPublicInstance | null,
  info: string
) => {
  console.error('Vue error:', err);
  console.error('Error info:', info);
  if (instance) {
    console.error(
      'Component:',
      instance.$options?.name ||
        (instance as unknown as { type?: { name?: string } }).type?.name ||
        'Unknown'
    );
  }
};

app
  .use(pinia)
  .use(Quasar, quasarUserOptions)
  .use(routes)
  .use(VueScrollTo)
  .use(components)
  .use(fontAwesome)
  .use(directives)
  .use(createMetaManager())
  .use(
    createGtm({
      id: 'GTM-P2C25ZS',
      enabled: import.meta.env.MODE === 'production',
      debug: false,
      vueRouter: routes as Router,
      ignoredViews: [],
    })
  )
  .mount('#app');
const authStore = useAuthStore();
authStore.init();
